import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// paths
import { paths } from 'src/routes/paths';
// logistics tabs layout
import LogisticsTabsLayout from 'src/sections/logistics/logistics-tabs-layout';
// roadmap tabs layout
import RoadmapTabsLayout from 'src/sections/roadmap/roadmap-tabs-layout';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
// novo layout com filtros
import DashboardPageLayout from 'src/sections/dashboard/page-layout';

// ----------------------------------------------------------------------

// Headers
import OverviewHeader from 'src/sections/overview/app/view/overview-header';
import ShipmentsHeader from 'src/sections/shipments/view/shipments-header';
import ManualOperationsHeader from 'src/sections/manual-operations/view/manual-operations-header';
import InventoryHeader from 'src/sections/inventory/view/inventory-header';

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
// SHIPMENT PAGE
const ShipmentDetailsPage = lazy(() => import('src/pages/dashboard/shipment/details'));
// MANUAL OPERATIONS PAGE
const ManualOperationsListPage = lazy(() => import('src/pages/dashboard/manual_operations/list'));
// INVENTORY PAGE
const InventoryListPage = lazy(() => import('src/pages/dashboard/inventory/list'));

const OperationsTimelinePage = lazy(() => import('src/pages/dashboard/operations-timeline'));
// SHIPMENTS PAGE
const ShipmentListPage = lazy(() => import('src/pages/dashboard/shipments/list'));
// LOGISTICS PAGE
const LogisticsFleetPage = lazy(() => import('src/pages/dashboard/logistics/fleet'));
// KANBAN PAGE
const KanbanPage = lazy(() => import('src/pages/dashboard/logistics/kanban'));

const DriversPage = lazy(() => import('src/pages/dashboard/logistics/drivers'));
const VehiclesPage = lazy(() => import('src/pages/dashboard/logistics/vehicles'));

const FleetMapPage = lazy(() => import('src/pages/dashboard/logistics/fleet-map'));
const CombinedMapPage = lazy(() => import('src/pages/dashboard/logistics/combined-map'));
const TripVisualizationPage = lazy(() => import('src/pages/dashboard/logistics/trips'));
const RouteAnalysisPage = lazy(() => import('src/pages/dashboard/logistics/route-analysis'));

const RoadmapPage = lazy(() => import('src/pages/dashboard/roadmap'));
const MySuggestionsPage = lazy(() => import('src/pages/dashboard/roadmap/my-suggestions'));
const TriagePage = lazy(() => import('src/pages/dashboard/roadmap/triage'));
const SuggestChatPage = lazy(() => import('src/pages/dashboard/roadmap/suggest'));
const FeatureDetailPage = lazy(() => import('src/pages/dashboard/roadmap/item'));
// FLEET DEBUG PAGE
const FleetDebugPage = lazy(() => import('src/pages/dashboard/fleet-debug/list'));
// LOGISTICS PAGE
const NotificationsPage = lazy(() => import('src/pages/dashboard/notifications/list'));
const LocationsPage = lazy(() => import('src/pages/dashboard/locations/list'));
// KARTRAK IA PAGE
const KartrakIAPage = lazy(() => import('src/pages/dashboard/kartrak-ia'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      // /dashboard (overview)
      {
        index: true,
        element: (
          <DashboardPageLayout 
            header={<OverviewHeader />}
            filtersConfig={{ showCancelledFilter: true, showHeaderOnHorizontal: true }}
          >
            <IndexPage />
          </DashboardPageLayout>
        ),
      },

      // /dashboard/shipments
      {
        path: 'shipments',
        element: (
          <DashboardPageLayout 
            header={<ShipmentsHeader />}
          >
            <ShipmentListPage />
          </DashboardPageLayout>
        ),
      },

      // /dashboard/manual_operations
      {
        path: 'manual_operations',
        element: (
          <DashboardPageLayout
            header={<ManualOperationsHeader />}
          >
            <ManualOperationsListPage />
          </DashboardPageLayout>
        ),
      },

      // /dashboard/inventory
      {
        path: 'inventory',
        element: (
          <DashboardPageLayout
            header={<InventoryHeader />}
          >
            <InventoryListPage />
          </DashboardPageLayout>
        ),
      },

      // /dashboard/operations-timeline — sem link em menu, acesso só por URL direta.
      // Controles próprios (uma usina + um dia), não usa o filtro global multi/range.
      {
        path: 'operations-timeline',
        element: <OperationsTimelinePage />,
      },

      // /dashboard/shipment/:id
      {
        path: 'shipment/:id',
        element: (
            <ShipmentDetailsPage />
        ),
      },

      // /dashboard/logistics (+ nested tabs)
      {
        path: 'logistics',
        element: <LogisticsTabsLayout />,
        children: [
          { element: <LogisticsFleetPage />, index: true },
          { path: 'map', element: <FleetMapPage /> },
          { path: 'combined-map', element: <CombinedMapPage /> },
          { path: 'kanban', element: <KanbanPage /> },
          { path: 'locations', element: <LocationsPage /> },
          { path: 'trips', element: <TripVisualizationPage /> },
          { path: 'route-analysis', element: <RouteAnalysisPage /> },
          { path: 'drivers', element: <DriversPage /> },
          { path: 'vehicles', element: <VehiclesPage /> },
        ],
      },

      // /dashboard/roadmap (+ nested tabs)
      {
        path: 'roadmap',
        element: <RoadmapTabsLayout />,
        children: [
          { element: <RoadmapPage />, index: true },
          { path: 'sugerir', element: <SuggestChatPage /> },
          { path: 'item/:featureId', element: <FeatureDetailPage /> },
          { path: 'my-suggestions', element: <MySuggestionsPage /> },
          { path: 'triage', element: <TriagePage /> },
        ],
      },

      // /dashboard/notifications
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },

      // legacy redirect: /dashboard/locations -> /dashboard/logistics/locations
      {
        path: 'locations',
        element: <Navigate to={paths.dashboard.logistics.locations} replace />,
      },

      // /dashboard/fleet-debug
      {
        path: 'fleet-debug',
        element: <FleetDebugPage />,
      },

      // /dashboard/kartrak-ia
      {
        path: 'kartrak-ia',
        element: <KartrakIAPage />,
      },
    ],
  },
];
